import { Alert, Autocomplete, Box, Button, Checkbox, FormControlLabel, Grid, Link, TextField } from '@mui/material';
import Modal from 'components/shared/Modal';
import React, { ChangeEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { defaultFormProps, getValidationProps } from 'util/Form';
import { defaultGridContainerProps, defaultGridItemProps, defaultSnackbarErrorProps } from 'util/Layout';
import { ModalProps } from 'store/types/ComponentProps';
import SelectOption from 'store/types/SelectOption';
import UserSearchFormItem from 'components/profile/UserSearchFormItem';
import CompanyService from 'services/api/CompanyService';
import { useSnackbar } from 'notistack';
import { UserJobFunction } from 'store/types/JobFunction';
import { UserContext } from 'components/UserGuard';
import { ParametersContext } from 'components/ParametersGuard';
import { UserEmployerAccessRequestsData } from 'store/types/UserEmployerAccessRequest';
import CompanyUser from 'store/types/CompanyUser';
import { isPendingRequestToCompanyExist } from 'util/Company';

import styles from './LinkCompanyUserModal.module.scss';
import commonStyles from 'styles/common.module.scss';

interface LinkCompanyUserModalProps extends ModalProps {
  companyId: string;
  portalRolesList: SelectOption[];
  refetch: () => void;
  onAddUserModalOpen: () => void;
  companyMembers?: CompanyUser[];
  companyRequests?: UserEmployerAccessRequestsData;
}

interface LinkCompanyUserFormValues {
  users: SelectOption[];
  isEmployee?: boolean;
  functionIds: string[];
}

const defaultValues: LinkCompanyUserFormValues = {
  users: [],
  isEmployee: false,
  functionIds: [],
};

const getCompanyName = (companyId: string, functions: UserJobFunction[] = []): string => {
  const foundCompany = functions.find((company) => company.companyId === companyId);

  return foundCompany ? foundCompany.companyName : '';
};

const LinkCompanyUserModal: React.FunctionComponent<LinkCompanyUserModalProps> = ({
  open,
  onClose,
  companyId,
  portalRolesList,
  refetch,
  onAddUserModalOpen,
  companyMembers,
  companyRequests,
}) => {
  const form = useForm<LinkCompanyUserFormValues>({ ...defaultFormProps, defaultValues });
  const { functions = [] } = useContext(UserContext);
  const {
    companyManagement: { searchAdminModalHeader },
  } = useContext(ParametersContext);
  const {
    handleSubmit,
    formState: { isValid },
    control,
    setValue,
    trigger,
    reset,
  } = form;
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState<boolean>(false);
  const [isPendingRequestExist, setIsPendingRequestExist] = useState<boolean>(false);
  const selectedUser = useWatch({ control, name: 'users' })?.[0];
  const isEmployee = useWatch({ control, name: 'isEmployee' }) ?? false;
  const companyName: string = useMemo(() => getCompanyName(companyId, functions), [functions, companyId]);

  const availablePortalRoles = useMemo(() => {
    if (!selectedUser?.id) {
      return portalRolesList;
    }

    const companyMember = companyMembers?.find((member) => member.id === selectedUser.id);

    const existingJobFunctionIds = new Set(
      companyMember?.functions?.map((customerFunction) => customerFunction.functionId) ?? []
    );

    return portalRolesList.filter((portalRole) => !existingJobFunctionIds.has(portalRole.id));
  }, [companyMembers, portalRolesList, selectedUser?.id]);

  useEffect(() => {
    setValue('functionIds', [], {
      shouldDirty: false,
      shouldValidate: true,
    });
  }, [selectedUser?.id, setValue]);

  useEffect(() => {
    void trigger('functionIds');
  }, [isEmployee, trigger]);

  useEffect(() => {
    setIsPendingRequestExist(
      Boolean(selectedUser?.id && companyRequests && isPendingRequestToCompanyExist(selectedUser.id, companyRequests))
    );
  }, [companyRequests, selectedUser?.id]);

  const handleLinkUserSubmit = useCallback(
    ({ users = [], isEmployee = false, functionIds = [] }: LinkCompanyUserFormValues) => {
      const selectedUser = users[0];

      if (!selectedUser?.id) {
        return;
      }

      setLoading(true);

      CompanyService.linkCompanyMember(companyId, selectedUser.id, functionIds, isEmployee)
        .then(() => {
          enqueueSnackbar('Request successfully added', {
            variant: 'success',
          });
          setLoading(false);
          onClose();
          refetch();
          reset(defaultValues);
        })
        .catch((errorMessage) => {
          enqueueSnackbar(errorMessage, defaultSnackbarErrorProps);
          setLoading(false);
        });
    },
    [companyId, enqueueSnackbar, onClose, refetch, reset]
  );

  const handleClose = useCallback(() => {
    onClose();
    reset(defaultValues);
  }, [onClose, reset]);

  return (
    <FormProvider {...form}>
      <Modal
        open={open}
        onClose={handleClose}
        maxWidth={'sm'}
        title={'Search for Person'}
        loading={loading}
        actions={
          <>
            <Button color={'secondary'} variant={'outlined'} onClick={handleClose}>
              {'Cancel'}
            </Button>
            <Button
              color={'secondary'}
              variant={'contained'}
              type={'submit'}
              disabled={!isValid}
              onClick={handleSubmit(handleLinkUserSubmit)}
            >
              {'Save'}
            </Button>
          </>
        }
      >
        <Grid {...defaultGridContainerProps} spacing={2}>
          <Grid {...defaultGridItemProps}>
            <p
              className={commonStyles.text}
              dangerouslySetInnerHTML={{
                __html: searchAdminModalHeader,
              }}
            />
          </Grid>
          <Grid {...defaultGridItemProps}>
            <h5 className={commonStyles.subTitle}>{'Company'}</h5>
            <TextField value={companyName} disabled={true} />
          </Grid>
          <Grid {...defaultGridItemProps}>
            <h5 className={commonStyles.subTitle}>{'Enter Email, First or Last Name to Search'}</h5>
            <UserSearchFormItem companyId={companyId} />
          </Grid>
          <Grid {...defaultGridItemProps}>
            <span className={styles.helperText}>{`Person not found? Click `}</span>
            <Link className={styles.link} onClick={onAddUserModalOpen}>
              {'here'}
            </Link>
          </Grid>
          {isPendingRequestExist && (
            <Grid {...defaultGridItemProps}>
              <Alert severity={'warning'}>{'Employee must accept your request to join this company.'}</Alert>
            </Grid>
          )}
          <Grid {...defaultGridItemProps}>
            <FormControlLabel
              label={'Is Employee?'}
              control={
                <Controller
                  render={({ field: { onChange, value, ref } }) => (
                    <Checkbox
                      checked={value}
                      indeterminate={isPendingRequestExist}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)}
                      color="primary"
                      ref={ref}
                      disabled={!selectedUser?.id || isPendingRequestExist}
                    />
                  )}
                  name={'isEmployee'}
                  control={control}
                />
              }
            />
          </Grid>
          <Grid {...defaultGridItemProps}>
            <h5 className={commonStyles.subTitle}>
              Select Portal Roles
              {!isEmployee && (
                <Box
                  component="span"
                  sx={{
                    color: 'error.main',
                    marginLeft: 0.5,
                  }}
                >
                  {'*'}
                </Box>
              )}
            </h5>
            <Controller
              name={'functionIds'}
              control={control}
              rules={{
                validate: (functionIds = []) =>
                  isEmployee || functionIds.length > 0 || 'Please select one or more Portal Roles.',
              }}
              render={({ field }) => {
                const selectedOptions = availablePortalRoles.filter(({ id }) => field.value?.includes(id));

                return (
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    disabled={!selectedUser?.id}
                    options={availablePortalRoles}
                    value={selectedOptions}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    onBlur={field.onBlur}
                    onChange={(_event, selectedRoles) => {
                      field.onChange(selectedRoles.map(({ id }) => id));
                    }}
                    ChipProps={{
                      color: 'secondary',
                      variant: 'filled',
                    }}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox checked={selected} sx={{ marginRight: 1 }} />
                        {option.name}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        {...getValidationProps('functionIds')}
                        inputRef={field.ref}
                        required={!isEmployee}
                        placeholder={'Please select one or more Portal Roles'}
                      />
                    )}
                  />
                );
              }}
            />
          </Grid>
        </Grid>
      </Modal>
    </FormProvider>
  );
};

export default LinkCompanyUserModal;
